import DeletingItemsTable from '@/common/components/notification-center/DeletingItemsTable.vue';
import FilesLoadingTable from '@/common/components/notification-center/FilesLoadingTable.vue';
import SlowRequestsDetails from '@/common/components/SlowRequestsDetails.vue';
import i18n from '@/common/i18n';
import store from '@/common/store';
import useAbgwStore from '@/common/store/pinia/abgw';
import useGlobalDialog from '@/common/composables/useGlobalDialog';
import useS3FilesStore from '@/common/store/pinia/s3Files';
import useComputeImagesStore from '@/common/store/pinia/computeImages';
import createStore from '@/common/store/createStore';
import { useNotificationsStore } from '@/common/store/pinia/notifications';
import useSlowRequestsStore from '@/common/store/pinia/slowRequests';
import CopyButton from '@/common/shared/CopyButton.vue';

const actionsMap = {
  abortImage: ({ globalConfirm }, { fileName, id }) => globalConfirm().create({
    title: i18n.t('Abort upload?'),
    content: i18n.t('Are you sure you want to abort the upload of "{name}"?', { name: fileName }),
    buttonCancel: i18n.t('Continue uploading'),
    buttonConfirm: i18n.t('Abort upload'),
    action: () => {
      const computeImagesStore = useComputeImagesStore();
      return computeImagesStore.abort(id);
    }
  }),
  openDeletingItemsDialog: (_ctx, { notificationId }) => {
    const globalDialog = useGlobalDialog();

    globalDialog.reveal({
      dialog: {
        title: i18n.t('Deleting items'),
      },
      component: {
        name: DeletingItemsTable,
        attrOrProp: {
          notificationId
        }
      },
      actions: [
        {
          slotName: 'footer',
          label: i18n.t('Close'),
          click: () => globalDialog.cancel()
        }
      ],
    });
  },
  openFileLoadingDialog: (_ctx, { notificationId }) => {
    const globalDialog = useGlobalDialog();
    const s3FilesStore = useS3FilesStore();

    globalDialog.reveal({
      dialog: {
        title: i18n.t('Uploading files'),
        widthSize: 'large',
      },
      component: {
        name: FilesLoadingTable,
      },
      actions: [
        {
          slotName: 'footer',
          label: i18n.t('Close'),
          click: () => globalDialog.cancel()
        },
        {
          slotName: 'footer-aside',
          label: i18n.t('Cancel all'),
          click: () => s3FilesStore.abortUploads(),
          type: 'secondary'
        }
      ],
    });
    const notificationsStore = useNotificationsStore();
    notificationsStore.dismiss(notificationId);
  },
  showSlowRequestsDetails: (_ctx, { notificationId }) => {
    const notificationsStore = useNotificationsStore();
    const globalDialog = useGlobalDialog();
    const slowRequestsStore = useSlowRequestsStore();

    globalDialog.reveal({
      dialog: {
        title: i18n.t('Request details'),
        widthSize: 'medium',
      },
      component: {
        name: SlowRequestsDetails,
      },
      actions: [
        {
          slotName: 'footer',
          label: i18n.t('Close'),
          click: () => globalDialog.cancel()
        },
        {
          slotName: 'footer-aside',
          component: CopyButton,
          label: i18n.t('Copy to clipboard'),
          dataTestId: 'copy-to-clipboard-button',
          attrOrProp: {
            text: JSON.stringify(slowRequestsStore.groupedSlowRequests, null, 2)
          }
        }
      ],
    });
    notificationsStore.dismiss(notificationId);
  },
  cancelUploads: () => {
    const s3FilesStore = useS3FilesStore();
    return s3FilesStore.abortUploads();
  },
  pauseUpdates: () => store.dispatch('updates/PAUSE'),
  cancelUpdates: () => store.dispatch('updates/CANCEL_UPDATES'),
  abgwRetryProgressProcess: (_ctx, payload) => {
    const abgwStore = useAbgwStore();
    return abgwStore.retryProgressProcess(payload);
  },
  abortServerMigration: ({ globalConfirm }, { serverId }) => {
    createStore(['servers']);

    const serverName = store.getters['servers/getItemById'](serverId)?.name;

    globalConfirm().create({
      title: i18n.t('Cancel migration'),
      content: i18n.t('Are you sure you want to cancel migration of the virtual machine "{name}"?', { name: serverName }),
      buttonCancel: i18n.t('Cancel'),
      buttonConfirm: i18n.t('Confirm'),
      disableSubmitButton: false,
      action: () => store.dispatch('servers/ABORT_MIGRATION', { id: serverId })
    });
  }
};

export default actionsMap;
